// CAPA GLOBAL DE ALERTAS — proyección de SOLO LECTURA.
// FUENTE EXPLÍCITA según escenario:
//   REAL → sondea GET /api/alarmas (read-model del backend, sin query params del cliente).
//   DEMO → lee las alarmas ficticias del ScenarioContext. NO se sondea el backend, NO hay
//          monitoreo real oculto, NINGUNA alarma real entra a lista/sonido/UI.
// El cambio de fuente (REAL↔DEMO, inst↔resid) es un ARRANQUE EN FRÍO: se limpia transición de
// cierre, silencio y sonido residual, y un `epoch` descarta respuestas reales en vuelo.
import { createContext, useContext, useCallback, useEffect, useRef, useState } from "react";
import api, { getAlarmas } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useScenario } from "@/context/ScenarioContext";
import * as alarmSound from "@/lib/alarmSound";
import * as sysNotify from "@/lib/systemNotify";
import { pushActivoLocal } from "@/lib/push/webpush";
import { verboEvento } from "@/lib/consolaV3/meta";

const AlertsContext = createContext(null);
const norm = (p) => (p === "maxima" ? "alta" : p);
const POLL_MS = 2000;
const SONIDO_KEY = "galarya_sonido_pref"; // preferencia ON/OFF del operador, persistente por navegador

export function AlertsProvider({ children }) {
  const { user } = useAuth();
  const { isDemo, demo } = useScenario();
  const [alarmasReal, setAlarmasReal] = useState([]);
  const [health, setHealth] = useState("reconectando"); // conectado | reconectando | sin_conexion | demo
  const [lastOk, setLastOk] = useState(0);
  const [audioEnabled, setAudioEnabled] = useState(() => localStorage.getItem(SONIDO_KEY) === "1");
  // Estado TÉCNICO del contexto Web Audio (null | running | suspended | interrupted | closed). La
  // preferencia ON no implica que el navegador permita reproducir: son dos hechos distintos.
  const [ctxState, setCtxState] = useState(() => alarmSound.contextState());
  const [mute, setMute] = useState(null); // { ids: Set<string>, expira: ms }
  const [closing, setClosing] = useState([]); // alarmas recién cerradas en tránsito
  const failRef = useRef(0);
  const muteRef = useRef(null); muteRef.current = mute;
  const epochRef = useRef(0);
  const ticketRef = useRef(0);
  // Motivos por los que la última lectura NO pudo agotar la cola (vacío = lista completa).
  const [listaParcial, setListaParcial] = useState([]);

  // Fuente activa: en DEMO las alarmas ficticias del contexto; en REAL, el snapshot sondeado.
  const alarmas = isDemo ? (demo?.alarmas || []) : alarmasReal;

  const poll = useCallback(async () => {
    const myEpoch = epochRef.current;
    const myTicket = (ticketRef.current += 1);
    try {
      const data = await getAlarmas();
      if (epochRef.current !== myEpoch) return; // respuesta en vuelo tras cambio de fuente → descartar
      if (myTicket !== ticketRef.current) return; // respuesta vieja: no pisa una más reciente
      setAlarmasReal(Array.isArray(data) ? data : []);
      setListaParcial(data?.incompleta ? (data.motivos || []) : []);
      setLastOk(Date.now());
      failRef.current = 0;
      setHealth("conectado");
    } catch {
      if (epochRef.current !== myEpoch || myTicket !== ticketRef.current) return;
      failRef.current += 1;
      setHealth(failRef.current >= 3 ? "sin_conexion" : "reconectando");
    }
  }, []);

  // Arranque en frío al cambiar de fuente: invalida epoch, limpia transición/silencio/sonido residual.
  useEffect(() => {
    epochRef.current += 1;
    ticketRef.current += 1;   // invalida cualquier lectura pendiente de la fuente anterior
    failRef.current = 0;
    setClosing([]);
    setMute(null);
    alarmSound.sync([]); // corta cualquier sonido de la fuente anterior
    if (isDemo) { setHealth("demo"); setAlarmasReal([]); }
    else { setHealth("reconectando"); }
    setListaParcial([]);
  }, [isDemo, demo?.key]);

  // Sondeo global SOLO en REAL con sesión. En DEMO no se sondea el backend.
  useEffect(() => {
    if (!user || isDemo) return;
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => clearInterval(t);
  }, [user, isDemo, poll]);

  // Heartbeat de sesión del operador (FIX A v2.8.7 — Central VESA). Central V3 no lo enviaba; ahora la
  // sesión REAL autenticada informa presencia/conectividad vía POST /api/sesion/heartbeat en el intervalo
  // configurado (sesion_heartbeat_seg). Proveedor global único => sin timers duplicados; limpia al
  // logout/unmount. NO crea semántica nueva: el vigilante ya NO libera por sesión vencida (FIX B).
  useEffect(() => {
    if (!user || isDemo) return;
    let intervalId = null;
    let cancelled = false;
    const ping = () => api.post("/sesion/heartbeat").catch(() => {});
    // H4 · `/config-operacion` es una ruta INTERNA: una cuenta external recibiría 403 en cada
    // arranque de sesión. El heartbeat (sí autorizado) usa el intervalo por defecto.
    const cadencia = user.actor_type === "external"
      ? Promise.resolve(15)
      : api.get("/config-operacion")
        .then(({ data }) => (data && data.sesion_heartbeat_seg) || 15)
        .catch(() => 15);
    cadencia
      .then((seg) => {
        if (cancelled) return;
        ping();
        intervalId = setInterval(ping, Math.max(5, seg) * 1000);
      });
    return () => { cancelled = true; if (intervalId) clearInterval(intervalId); };
  }, [user, isDemo]);

  // El sonido depende del ESTADO de la FUENTE ACTIVA ('recibida'). El silencio temporal aplica solo
  // al conjunto capturado; una alarma nueva posterior SÍ suena.
  useEffect(() => {
    if (!audioEnabled) return;
    const m = muteRef.current;
    const activo = m && Date.now() <= m.expira ? m.ids : null;
    const audibles = activo ? alarmas.filter((a) => !activo.has(a.id)) : alarmas;
    alarmSound.sync(audibles);
  }, [alarmas, audioEnabled, mute]);

  useEffect(() => {
    if (!mute) return;
    const t = setInterval(() => { if (Date.now() > mute.expira) setMute(null); }, 1000);
    return () => clearInterval(t);
  }, [mute]);

  useEffect(() => () => alarmSound.disableAudio(), []);

  // Preferencia de sonido persistente (por navegador). Si estaba ON, se intenta rehabilitar el audio
  // al montar; el navegador puede exigir un gesto para reproducir, pero la PREFERENCIA se conserva ON.
  // Encontrar "1" en localStorage NO basta para declarar «activado y reproducible»: eso lo dice el
  // estado real del contexto, que se observa aquí.
  useEffect(() => {
    if (localStorage.getItem(SONIDO_KEY) === "1") alarmSound.enableAudio();
    setCtxState(alarmSound.contextState());
  }, []);
  useEffect(() => {
    const off = alarmSound.subscribe((s) => setCtxState(s));
    const onVisible = () => setCtxState(alarmSound.contextState());
    document.addEventListener("visibilitychange", onVisible);
    return () => { off(); document.removeEventListener("visibilitychange", onVisible); };
  }, []);
  // Sincroniza la preferencia entre pestañas Galarya del mismo navegador/perfil.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== SONIDO_KEY) return;
      const on = e.newValue === "1";
      setAudioEnabled(on);
      if (on) alarmSound.enableAudio(); else alarmSound.disableAudio();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Transición de cierre (solo REAL). Que una alarma DEJE DE APARECER en una respuesta de lista NO
  // acredita su cierre: pudo no alcanzarse en ese recorrido (p. ej. un caso que escala durante la
  // paginación y vuelve en el siguiente), incluso cuando el recorrido terminó sin motivos de
  // incompletitud. La transición se apoya ÚNICAMENTE en un cierre EXPLÍCITO ya presente en los
  // datos recibidos; sin esa confirmación se omite y no se consulta caso por caso.
  useEffect(() => {
    if (isDemo || health !== "conectado") return;
    const now = Date.now();
    const cerradas = alarmas
      .filter((a) => a.estado === "cerrada")
      .map((a) => ({ ...a, _cerrada: true,
                     _cerrada_por: a.cerrada_por_nombre || a.cierre_operador_nombre || null,
                     _expira: now + 3500 }));
    if (!cerradas.length) return;
    setClosing((c) => {
      const vig = c.filter((x) => x._expira > now);
      const ids = new Set(vig.map((x) => x.id));
      return [...vig, ...cerradas.filter((x) => !ids.has(x.id))];
    });
  }, [alarmas, health, isDemo]);

  useEffect(() => {
    if (!closing.length) return;
    const t = setInterval(() => setClosing((c) => c.filter((x) => x._expira > Date.now())), 400);
    return () => clearInterval(t);
  }, [closing.length]);

  // La PREFERENCIA del operador se guarda siempre que él la pide; que el navegador permita o no
  // reproducir se refleja aparte en `audioState` (nunca se reporta un ON reproducible falso).
  const enableAudio = useCallback(() => {
    const ok = alarmSound.enableAudio();
    setAudioEnabled(true);
    localStorage.setItem(SONIDO_KEY, "1");
    setCtxState(alarmSound.contextState());
    return ok;
  }, []);
  const disableAudio = useCallback(() => { alarmSound.disableAudio(); setAudioEnabled(false); localStorage.setItem(SONIDO_KEY, "0"); setCtxState(alarmSound.contextState()); }, []);
  const testSound = useCallback(() => { if (!audioEnabled) { const ok = alarmSound.enableAudio(); setAudioEnabled(ok); } alarmSound.test(); setCtxState(alarmSound.contextState()); }, [audioEnabled]);
  // Reanudación del contexto desde un gesto del operador (estado BLOQUEADO POR EL NAVEGADOR).
  const resumeAudio = useCallback(async () => {
    const ok = await alarmSound.resume();
    if (ok) alarmSound.enableAudio();
    setCtxState(alarmSound.contextState());
    return ok;
  }, []);
  // Prueba aislada con el sonido SILENCIADO: un tono, sin tocar la preferencia ni el loop.
  const testSoundOnce = useCallback(async () => {
    const ok = await alarmSound.testOnce();
    setCtxState(alarmSound.contextState());
    return ok;
  }, []);
  // TRES ESTADOS REALES: silenciado por el operador · activado y reproducible · bloqueado.
  const audioState = !audioEnabled ? "muted" : (ctxState === "running" ? "on" : "blocked");

  const muteCurrent = useCallback((min = 5) => {
    const ids = new Set(alarmas.filter((a) => a.estado === "recibida").map((a) => a.id));
    setMute({ ids, expira: Date.now() + min * 60000 });
  }, [alarmas]);
  const clearMute = useCallback(() => setMute(null), []);

  const activas = alarmas.filter((a) => a.estado !== "cerrada");
  const altaSinTomar = activas.filter((a) => norm(a.prioridad) === "alta" && a.estado === "recibida");
  const mediaSinTomar = activas.filter((a) => norm(a.prioridad) === "media" && a.estado === "recibida");
  const enAtencion = activas.filter((a) => a.estado !== "recibida");
  const baja = activas.filter((a) => norm(a.prioridad) === "baja");
  const muteActivo = mute && Date.now() <= mute.expira ? mute : null;

  // Aviso visual GLOBAL en el título del tab (funciona en background/sin foco/sonido OFF). Fuente
  // única de "alertas accionables": las alarmas activas de useAlerts (los eventos BAJA NO cuentan).
  useEffect(() => {
    const n = activas.length;
    document.title = n === 0
      ? "Consola Galarya Monitoreo"
      : `🔴 ${n} ${n === 1 ? "ALERTA" : "ALERTAS"} · Galarya Monitoreo`;
  }, [activas.length]);

  // NOTIFICACIÓN DEL SISTEMA EN SEGUNDO PLANO — superficie adicional de ESTA MISMA capa global.
  // ELEGIBILIDAD: exactamente el conjunto que ya interrumpe globalmente (altaSinTomar +
  // mediaSinTomar de la fuente autoritativa). BAJA y eventos informativos NO entran; las alarmas
  // técnicas no reciben trato especial: siguen su prioridad autoritativa. No se recalcula nada.
  const notificadasRef = useRef(new Set()); // identidad autoritativa (a.id) ya entregada
  const sembradoRef = useRef(false);
  const [permisoNotif, setPermisoNotif] = useState(() => sysNotify.permiso());

  // Arranque en frío (cambio de fuente o de sesión): se re-siembra, no se re-notifica el historial.
  useEffect(() => {
    notificadasRef.current = new Set();
    sembradoRef.current = false;
  }, [isDemo, demo?.key, user?.id]);

  useEffect(() => {
    if (!user || isDemo) return;          // DEMO nunca emite notificaciones del sistema
    if (health !== "conectado") return;   // sin snapshot autoritativo no se notifica
    const elegibles = [...altaSinTomar, ...mediaSinTomar];
    const vivas = new Set(elegibles.map((a) => a.id));
    notificadasRef.current.forEach((id) => { if (!vivas.has(id)) notificadasRef.current.delete(id); });
    // Primer snapshot conectado de la sesión: siembra el estado ya existente sin notificar
    // (reconexión y refresh reconstruyen desde el snapshot y NO deben bombardear).
    // La alarma activa NO se pierde: sigue en la barra global, el contador y el sonido.
    // En foreground manda la superficie actual (barra/sonido/cola): se marca como entregada.
    const soloSembrar = !sembradoRef.current || !sysNotify.enSegundoPlano();
    sembradoRef.current = true;
    // NOTIF-INST-01 · si ESTE dispositivo ya recibe Web Push, el aviso local se calla: la
    // notificación la entrega el Service Worker y no se duplica en la misma PWA.
    const hayPush = pushActivoLocal(user?.id);
    elegibles.forEach((a) => {
      if (notificadasRef.current.has(a.id)) return;
      notificadasRef.current.add(a.id);
      if (soloSembrar || hayPush) return;
      const pr = norm(a.prioridad) === "alta" ? "ALTA" : "MEDIA";
      const quien = a.persona_nombre || a.sitio_nombre || "Atribución por sitio";
      const donde = [a.sitio_nombre, a.zona_nombre].filter(Boolean).join(" · ");
      sysNotify.notificar({
        id: a.id,
        titulo: `${pr} · ${verboEvento(a.tipo)}`,
        cuerpo: [quien, donde].filter(Boolean).join(" — ") || "Galarya Monitoreo",
      });
    });
  }, [alarmas, health, isDemo, user, altaSinTomar, mediaSinTomar]);

  const solicitarPermisoNotif = useCallback(async () => {
    const p = await sysNotify.solicitarPermiso();
    setPermisoNotif(p);
    return p;
  }, []);

  const value = {
    alarmas, activas, health, lastOk, closing, isDemo,
    altaSinTomar, mediaSinTomar, enAtencion, baja,
    audioEnabled, enableAudio, disableAudio, testSound,
    audioState, resumeAudio, testSoundOnce,
    muteActivo, muteCurrent, clearMute,
    listaParcial, listaIncompleta: !isDemo && listaParcial.length > 0,
    permisoNotif, solicitarPermisoNotif, notificacionesSoportadas: sysNotify.soporta(),
  };
  return <AlertsContext.Provider value={value}>{children}</AlertsContext.Provider>;
}

export function useAlerts() {
  const ctx = useContext(AlertsContext);
  if (!ctx) throw new Error("useAlerts debe usarse dentro de AlertsProvider");
  return ctx;
}
